import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, CaretDown, CaretRight, Warning, X, Sparkle, Users, ChartLineUp, ArrowsOut, MagnifyingGlass, Wrench } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { humanStackLabel } from "@/lib/stackLabel";
import { TID } from "@/constants/testIds";
import LateInjuryModal from "@/components/LateInjuryModal";
import DiversityCard from "@/components/DiversityCard";
import ShortfallBanner from "@/components/ShortfallBanner";
import PortfolioHealth from "@/components/PortfolioHealth";
import SundayBanner from "@/components/SundayBanner";
import CaptainShareChart from "@/components/CaptainShareChart";
import { TeamStackHeatmap } from "@/components/TeamStackHeatmap";
import { PlayerHeadshot, useHeadshotMap } from "@/components/TeamVisuals";
import { teamTextColor } from "@/lib/teams";
import ResultsScoreboard from "@/components/ResultsScoreboard";
import { CandidateExplorer } from "@/components/CandidateExplorer";
import FixLineupPanel from "@/components/FixLineupPanel";


// Sprint 60.140 — Lightweight stack summarizer for Raw lineups.
// Raw lineups don't ship a `stack_desc` from the backend the way Smart
// does (Smart layers stack heuristics through Combat Stack Matrix).
// This gives users a readable "QB+2WR" / "Bring-Back" / "Team stack"
// label for each Raw lineup so both source tables render consistently.
function _summarizeStack(players) {
  if (!Array.isArray(players) || !players.length) return "—";
  const byTeam = {};
  const qb = players.find((p) => (p.position || "").toUpperCase() === "QB");
  if (!qb) return "—";
  for (const p of players) {
    const t = p.team || "?";
    const pos = (p.position || "").toUpperCase();
    if (pos === "DST") continue;
    byTeam[t] = (byTeam[t] || 0) + 1;
  }
  const qbTeam = qb.team;
  const withQb = byTeam[qbTeam] || 1;
  // Opponent team of QB (used to detect bring-back)
  // Best-effort: any opp on same game_id as QB.
  const oppTeam = qb.opp_team || qb.opp || null;
  const bringBack = oppTeam ? (byTeam[oppTeam] || 0) : 0;
  const stackPart = `QB+${withQb - 1}`;
  if (bringBack > 0) return `${stackPart} · Bring-Back +${bringBack}`;
  return stackPart;
}


export default function Lineups() {
  const [data, setData] = useState(null);
  const headshots = useHeadshotMap();
  const [rawData, setRawData] = useState(null);
  // Sprint 60.140 — Source toggle. Users optimize twice (Smart + Raw) and
  // need one shared "My Lineups" page to compare them at a glance rather
  // than a full-page view-all that lands without a tab context.
  const [autoData, setAutoData] = useState(null);
  const [contestFilter, setContestFilter] = useState("all");
  const [source, setSource] = useState(() => {
    try { return localStorage.getItem("wp_lineups_source") || "smart"; }
    catch { return "smart"; }
  });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [showInjury, setShowInjury] = useState(false);
  const [pool, setPool] = useState([]);
  const [activeOuts, setActiveOuts] = useState([]);
  const [diversity, setDiversity] = useState(null);
  const [ranges, setRanges] = useState(null);
  const [rangesLoading, setRangesLoading] = useState(false);
  // Focused player filter — when set, only show lineups containing this player.
  // Clicking the magnifying glass on any player row toggles the focus. Perfect
  // for "show me every lineup I'm running Christian McCaffrey in" workflows.
  const [focusedPlayer, setFocusedPlayer] = useState(null); // { dk_id, name } | null
  // Sprint 60.135 — Lineups sort key. Default "proj" preserves ILP order.
  // "sim_win_rate" reorders by portfolio-relative simulated crown probability
  // (only meaningful for MILLY/GPP builds; other builds fall back to proj).
  const [sortBy, setSortBy] = useState("proj");
  // Sprint 62.1 — lineup_id currently showing the Fix panel (Smart only)
  const [fixingId, setFixingId] = useState(null);

  // Persist source toggle across sessions.
  useEffect(() => {
    try { localStorage.setItem("wp_lineups_source", source); }
    catch { /* private mode */ }
  }, [source]);

  const load = async () => {
    try {
      const [res, rawRes, poolRes, summary, autoRes] = await Promise.all([
        api.getLineups(),
        api.getRawLineups().catch(() => ({ lineups: [], count: 0 })),
        api.getPool(500),
        api.summary(),
        api.entryTemplateLineups().catch(() => ({ lineups: [], count: 0, contests: [] })),
      ]);
      setAutoData(autoRes && autoRes.count ? autoRes : null);
      setData(res);
      // Normalize raw lineups to match the shape Smart uses so the shared
      // table code can render them without a divergent branch. Raw lacks
      // sim/sim_win_rate/stack_type; we synthesize `lineup_id` from index,
      // `total_own` from `own_sum`, and mark `_source: "raw"` so the
      // renderer can hide sim-only columns.
      const rawNormalized = (rawRes?.lineups || []).map((L, i) => ({
        lineup_id: L.lineup_id || (i + 1),
        players: L.players || [],
        projection: L.projection || 0,
        salary: L.salary || 0,
        total_own: L.own_sum ?? L.total_own ?? 0,
        stack_desc: L.stack_desc || _summarizeStack(L.players),
        stack_type: L.stack_type || null,
        sim: null,
        sim_win_rate: null,
        sim_win_rank: null,
        expected_dupes: null,
        _source: "raw",
      }));
      const rawExposure = {};
      for (const L of rawNormalized) {
        for (const p of (L.players || [])) {
          if (!p.dk_id) continue;
          rawExposure[String(p.dk_id)] = (rawExposure[String(p.dk_id)] || 0) + 1;
        }
      }
      setRawData({
        lineups: rawNormalized,
        count: rawNormalized.length,
        exposure: rawExposure,
        request: rawRes?.request || {},
      });
      // Auto-expand every lineup by default so users see the actual 9-man
      // roster without a click. Users can still collapse individual rows if
      // they want a denser overview.
      const allExpanded = {};
      for (const L of (res?.lineups || [])) allExpanded[L.lineup_id] = true;
      for (const L of rawNormalized) allExpanded[`raw-${L.lineup_id}`] = true;
      setExpanded(allExpanded);
      setPool(poolRes.rows || []);
      setActiveOuts(summary.late_injury_outs || []);
      if (res?.count > 0) {
        api.lineupsDiversity().then(setDiversity).catch(() => setDiversity(null));
        setRangesLoading(true);
        api.lineupsRangeOfOutcomes(25000)
          .then((r) => setRanges(r))
          .catch(() => setRanges(null))
          .finally(() => setRangesLoading(false));
      }
    } catch (e) {
      console.debug("lineups load failed:", e?.message || e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const clearOut = async (dkId) => {
    await api.clearLateOut(dkId);
    toast.info("Injury out cleared");
    load();
  };

  // Skeleton while the initial fetch is in flight — prevents the "No lineups
  // yet" empty state from flashing before real data arrives (the ~500ms
  // Promise.all round-trip). Once loading resolves, either the empty state
  // or the full table renders in its stead.
  if (loading) {
    return (
      <div>
        <ActionBar onInjury={() => setShowInjury(true)} activeOuts={activeOuts} onClear={clearOut} pool={pool} />
        <div
          data-testid="lineups-loading-state"
          className="bg-[#121212] border border-zinc-800 p-12 text-center mt-4"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#39FF14]/10 border border-[#39FF14]/30 mb-6 animate-pulse">
            <Sparkle size={28} className="text-[#39FF14]" weight="fill" />
          </div>
          <div className="heading text-xl font-bold text-white mb-2">Loading your portfolio…</div>
          <div className="text-sm text-zinc-400 max-w-md mx-auto leading-relaxed">
            Fetching lineups, diversity metrics, and 25K Monte Carlo range-of-outcomes.
          </div>
        </div>
      </div>
    );
  }

  if (!data || (data.count === 0 && (!rawData || rawData.count === 0) && (!autoData || autoData.count === 0))) {
    return (
      <div>
        <ActionBar onInjury={() => setShowInjury(true)} activeOuts={activeOuts} onClear={clearOut} pool={pool} />
        <div
          data-testid="lineups-empty-state"
          className="bg-[#121212] border border-zinc-800 p-12 text-center mt-4"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#39FF14]/10 border border-[#39FF14]/30 mb-6">
            <Sparkle size={28} className="text-[#39FF14]" weight="fill" />
          </div>
          <div className="heading text-xl font-bold text-white mb-2">No lineups yet</div>
          <div className="text-sm text-zinc-300 mb-6 max-w-md mx-auto leading-relaxed">
            Run either optimizer to generate your portfolio. Smart Optimizer applies WarPaint's
            contest-shaped ruleset (stack shapes, Vegas-favored QBs, forced low-owned pivots); Raw
            Optimizer is a pure ILP that respects only your explicit locks, excludes, and group rules.
          </div>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button
              data-testid="lineups-goto-optimizer"
              onClick={() => window.dispatchEvent(new CustomEvent("wp:goto-tab", { detail: { tab: "optimizer" } }))}
              className="inline-flex items-center gap-2 bg-[#39FF14] text-black px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#2ee30d] shadow-[0_0_24px_-6px_#39FF14] transition-colors"
            >
              <Sparkle size={12} weight="fill" />
              Smart Optimizer
            </button>
            <button
              data-testid="lineups-goto-raw"
              onClick={() => window.dispatchEvent(new CustomEvent("wp:goto-tab", { detail: { tab: "raw_optimizer" } }))}
              className="inline-flex items-center gap-2 bg-transparent border border-[#39FF14] text-[#39FF14] px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#39FF14]/10 transition-colors"
            >
              <Sparkle size={12} weight="fill" />
              Manual Optimizer
            </button>
          </div>
        </div>
        <div className="mt-4">
          {/* Sprint 60.67 — Chrome extension removed per gambling
              compliance rules. Users now download the DK Entry CSV,
              upload here, generate lineups, and re-upload to DK. */}
        </div>
      <LateInjuryModal open={showInjury} onClose={() => setShowInjury(false)} pool={pool} onApplied={load} />
      </div>
    );
  }

  // Sprint 60.140 — Active dataset driven by the source toggle. Every
  // downstream computation (exposure rows, table sort, range map) reads
  // from `activeData` so the same UI renders both Smart and Raw
  // portfolios without a divergent code path.
  const autoFiltered = (() => {
    if (!autoData) return { lineups: [], count: 0, exposure: {}, contests: [] };
    if (contestFilter === "all") return autoData;
    const lineups = (autoData.lineups || []).filter((L) => String(L.contest_id) === contestFilter);
    const exposure = {};
    for (const L of lineups) for (const p of (L.players || [])) {
      if (p.dk_id != null) exposure[String(p.dk_id)] = (exposure[String(p.dk_id)] || 0) + 1;
    }
    return { ...autoData, lineups, count: lineups.length, exposure };
  })();
  const activeData = source === "raw"
    ? (rawData || { lineups: [], count: 0, exposure: {} })
    : source === "auto"
      ? autoFiltered
      : data;
  const activeCount = activeData?.count || 0;
  const otherCount = source === "raw" ? (data?.count || 0) : (rawData?.count || 0);

  const exposureRows = Object.entries(activeData.exposure || {})
    .map(([id, count]) => {
      const player = activeData.lineups.flatMap((l) => l.players).find((p) => String(p.dk_id) === String(id));
      return {
        id, name: player?.name || id, team: player?.team || "", pos: player?.position || "",
        salary: player?.salary || 0, count, pct: (count / Math.max(1, activeData.count)) * 100,
      };
    })
    .sort((a, b) => b.count - a.count);

  // Build a lineup_id -> mc outcomes map for fast lookup in the table.
  // Range-of-outcomes only exists for Smart lineups (Raw doesn't run
  // deep sims by default) so we only align when the active source is
  // Smart. Raw table cells default to "—".
  const rangeByLineupId = {};
  if (source === "smart") {
    const rangeList = ranges?.outcomes || [];
    (activeData.lineups || []).forEach((L, i) => {
      if (rangeList[i]) rangeByLineupId[L.lineup_id] = rangeList[i];
    });
  }

  // DraftKings canonical roster order: QB, RB, RB, WR, WR, WR, TE, FLEX, DST.
  // Pro DFS players read rosters top-down in this exact order — anything else
  // is friction. FLEX is whichever extra RB/WR/TE slot exists after filling
  // the base positional minimums (1 QB, 2 RB, 3 WR, 1 TE, 1 DST).
  const sortLineupPlayers = (players) => {
    // Showdown lineups use CPT + FLEX slots — keep CPT first, then FLEX rows.
    const isShowdown = players.some((p) => p.roster_slot === "CPT");
    if (isShowdown) {
      const cpt = players.filter((p) => p.roster_slot === "CPT");
      const flex = players.filter((p) => p.roster_slot !== "CPT");
      return [...cpt, ...flex];
    }
    // Sort each position group by projection desc so the highest-priced /
    // highest-projected RB/WR gets the primary slot; the "extra" one falls to
    // FLEX at the end, which matches how DK's export renders lineups.
    const byPos = { QB: [], RB: [], WR: [], TE: [], DST: [] };
    for (const p of players) {
      const pos = (p.position || "").toUpperCase();
      if (byPos[pos]) byPos[pos].push(p);
    }
    for (const k of Object.keys(byPos)) {
      byPos[k].sort((a, b) => (Number(b.projection || b.our_proj || 0) - Number(a.projection || a.our_proj || 0)));
    }
    const qb = byPos.QB.slice(0, 1);
    const rb = byPos.RB.slice(0, 2);
    const wr = byPos.WR.slice(0, 3);
    const te = byPos.TE.slice(0, 1);
    const dst = byPos.DST.slice(0, 1);
    const used = new Set([...qb, ...rb, ...wr, ...te, ...dst].map((p) => p.dk_id));
    // FLEX = whichever extra RB/WR/TE remains
    const flex = [...byPos.RB, ...byPos.WR, ...byPos.TE].filter((p) => !used.has(p.dk_id)).slice(0, 1);
    return [...qb, ...rb, ...wr, ...te, ...flex, ...dst];
  };

  return (
    <div className="space-y-4">
      <ActionBar
        onInjury={() => setShowInjury(true)}
        activeOuts={activeOuts}
        onClear={clearOut}
        pool={pool}
        rightSlot={
          <a
            data-testid={TID.exportCsvBtn}
            href={api.exportURL()}
            className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-[#2ee30d] text-black font-bold uppercase tracking-widest text-xs py-3 px-5 transition-colors"
          >
            <Download size={14} weight="bold" />
            Download DK CSV
          </a>
        }
      />

      <SourceToggleAndCompare
        source={source}
        setSource={setSource}
        smart={data}
        raw={rawData}
        auto={autoData}
        contestFilter={contestFilter}
        setContestFilter={setContestFilter}
      />

      <SundayBanner source={source} onFixed={() => load()} />
      {(activeData?.lineups || []).length > 0 && !(activeData.lineups || []).some((L) => (L.players || []).some((p) => p.roster_slot === "CPT")) && (
        <TeamStackHeatmap lineups={activeData.lineups} />
      )}
      {(activeData?.lineups || []).some((L) => (L.players || []).some((p) => p.roster_slot === "CPT")) && (
        <CaptainShareChart
          lineups={activeData.lineups}
          overrides={(source === "raw" ? {} : (activeData?.config?.player_overrides || {}))}
          cptMinPct={source === "raw" ? (activeData?.request?.cpt_min_exposures || {}) : {}}
        />
      )}
      {source === "smart" && (
        <RangeOfOutcomesCard ranges={ranges} loading={rangesLoading} />
      )}

      {/* Shortfall banner — Smart-only (Raw doesn't ship a `requested`
          target — the Raw ILP either delivers the ask or 0). Persistent,
          dismissible, and includes the actionable reason from the solver. */}
      {source === "smart" && data.requested > 0 && data.count < data.requested && (
        <ShortfallBanner
          requested={data.requested}
          count={data.count}
          notes={data.generation_notes || []}
          baseConfig={data.config || {}}
          onFilled={() => load()}
        />
      )}

      {source === "smart" && diversity && <DiversityCard diversity={diversity} onRefresh={load} />}
      {source === "smart" && <PortfolioHealth lineups={data?.lineups || []} />}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <section className="xl:col-span-8 bg-[#121212] border border-zinc-800 overflow-hidden">
          <div className="px-4 py-2 border-b border-zinc-800 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="overline text-[10px] text-zinc-400">
                Your Lineups
              </div>
              {focusedPlayer && (
                <button
                  type="button"
                  data-testid="focused-player-chip"
                  onClick={() => setFocusedPlayer(null)}
                  title={`Clear filter — click to show all lineups again`}
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-[#39FF14]/10 border border-[#39FF14]/50 text-[#39FF14] mono text-[10px] uppercase tracking-widest hover:bg-[#39FF14]/20 transition-colors"
                >
                  <MagnifyingGlass size={10} weight="fill" />
                  Focused: {focusedPlayer.name}
                  <X size={10} weight="bold" className="ml-0.5" />
                </button>
              )}
            </div>
            <button
              type="button"
              data-testid="lineup-toggle-all"
              onClick={() => {
                const allOpen = activeData.lineups.every((L) => expanded[L.lineup_id]);
                const next = {};
                if (!allOpen) for (const L of activeData.lineups) next[L.lineup_id] = true;
                setExpanded(next);
              }}
              className="mono text-[9px] uppercase tracking-widest text-zinc-500 hover:text-[#39FF14] transition-colors"
              title="Collapse or expand every lineup at once"
            >
              {activeData.lineups.every((L) => expanded[L.lineup_id]) ? "↕ Collapse all" : "↕ Expand all"}
            </button>
          </div>
          <div className="max-h-[720px] overflow-y-auto overflow-x-auto">
            <table data-testid={TID.lineupsTable} className="w-full min-w-[720px] text-xs mono">
              <thead className="bg-black sticky top-0">
                <tr className="text-left text-zinc-300 uppercase tracking-widest text-[10px]">
                  <th className="px-3 py-2 w-8"></th>
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Stack</th>
                  {source === "auto" && <th className="px-3 py-2" title="The DraftKings tournament this entry is in">Contest</th>}
                  <th className="px-3 py-2">Sal</th>
                  <th className="px-3 py-2">Proj</th>
                  <th className="px-3 py-2" title="95th percentile Monte Carlo score">Ceil</th>
                  <th className="px-3 py-2" title="5th percentile Monte Carlo score">Floor</th>
                  <th
                    onClick={() => setSortBy(sortBy === "sim_win_rate" ? "proj" : "sim_win_rate")}
                    data-testid="lineup-sort-sim-win"
                    className={`px-3 py-2 cursor-pointer transition-colors select-none ${
                      sortBy === "sim_win_rate" ? "text-[#39FF14] font-bold" : "hover:text-white"
                    }`}
                    title="Sprint 60.134 · Portfolio-relative sim-win-rate. Click to sort the batch by simulated crown probability (MILLY/GPP builds only). Click again to restore Proj order."
                  >
                    $EV / Field Win {sortBy === "sim_win_rate" ? "▾" : "⇅"}
                  </th>
                  <th className="px-3 py-2" title="Total lineup ownership sum. Green ≤90% = optimal barbell leverage. Yellow 90–100% = neutral. Red >100% = high duplication risk.">Own%</th>
                  {source === "smart" && <th className="px-3 py-2 text-center" title="Re-solve just this lineup (swap an injured player or rebuild the line) without touching the rest of your portfolio">Fix</th>}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Sprint 60.135 — Optional Sim Win % sort. Non-mutating slice so
                  // toggling back to Proj restores the ILP order.
                  const base = focusedPlayer
                    ? activeData.lineups.filter((L) => L.players.some((p) => String(p.dk_id) === String(focusedPlayer.dk_id)))
                    : activeData.lineups;
                  const rows = sortBy === "sim_win_rate"
                    ? [...base].sort((a, b) => (Number(b.payout_ev ?? b.sim_win_rate ?? 0) - Number(a.payout_ev ?? a.sim_win_rate ?? 0)))
                    : base;
                  return rows;
                })().map((L) => {
                  const mc = rangeByLineupId[L.lineup_id];
                  return (
                  <React.Fragment key={L.lineup_id}>
                    <tr
                      onClick={() => setExpanded({ ...expanded, [L.lineup_id]: !expanded[L.lineup_id] })}
                      className="border-t border-zinc-900 hover:bg-zinc-900/40 cursor-pointer"
                      data-testid={`lineup-row-${L.lineup_id}`}
                      title={expanded[L.lineup_id] ? "Click to collapse" : "Click to view the 9-man roster"}
                    >
                      <td className="px-3 py-2 text-zinc-400">
                        {expanded[L.lineup_id] ? <CaretDown size={14} weight="bold" className="text-[#39FF14]" /> : <CaretRight size={14} weight="bold" />}
                      </td>
                      <td className="px-3 py-2 font-bold">{L.lineup_id}</td>
                      <td className="px-3 py-2">
                        <StackTypeBadge lineup={L} />
                      </td>
                      <td className="px-3 py-2 text-zinc-300">{L.stack_desc}</td>
                      {source === "auto" && (
                        <td className="px-3 py-2 text-zinc-400 max-w-[220px] truncate"
                            title={L.contest_name || ""}
                            data-testid={`lineup-contest-${L.lineup_id}`}>
                          {L.contest_name || "—"}
                        </td>
                      )}
                      <td className="px-3 py-2">${L.salary.toLocaleString()}</td>
                      <td className="px-3 py-2 text-[#39FF14] font-bold">{L.projection.toFixed(1)}</td>
                      <td className="px-3 py-2 text-[#39FF14]">{L.sim?.ceiling?.toFixed(1) || "—"}</td>
                      <td className="px-3 py-2 text-zinc-500">{L.sim?.floor?.toFixed(1) || "—"}</td>
                      <td
                        className={`px-3 py-2 font-bold mono ${
                          L.sim_win_rank === 1 ? "text-[#39FF14]" :
                          (L.sim_win_rank || 999) <= 3 ? "text-[#FFCC00]" :
                          "text-zinc-400"
                        }`}
                        title={L.payout_ev != null
                          ? `$EV rank ${L.sim_win_rank} · $${Number(L.payout_ev).toFixed(2)} expected payout (ROI ${(L.roi * 100).toFixed(0)}%) · wins ${(L.sim_win_rate * 100).toFixed(2)}% · top 1% in ${((L.p_top1pct || 0) * 100).toFixed(1)}% · cashes ${((L.p_cash || 0) * 100).toFixed(0)}%${L.expected_dupes != null ? ` · ~${Number(L.expected_dupes).toFixed(2)} expected duplicates` : ""}`
                          : L.sim_win_rank ? `Field-EV rank ${L.sim_win_rank} · clears the field's winning bar in ${(L.sim_win_rate * 100).toFixed(2)}% of correlated sims${L.expected_dupes != null ? ` · ~${Number(L.expected_dupes).toFixed(2)} expected duplicates` : ""}` : "GPP-only metric"}
                        data-testid={`lineup-sim-win-${L.lineup_id}`}
                      >
                        {L.payout_ev != null
                          ? <span data-testid={`lineup-roi-${L.lineup_id}`}>{`${L.roi >= 0 ? "+" : ""}${(L.roi * 100).toFixed(0)}% ROI`}</span>
                          : L.sim_win_rate != null ? `${(L.sim_win_rate * 100).toFixed(1)}%` : "—"}
                        {L.sim_win_rank === 1 && <span className="ml-1 text-[8px]">👑</span>}
                      </td>
                      <td className="px-3 py-2">{L.total_own.toFixed(1)}%</td>
                      {source === "smart" && (
                        <td className="px-3 py-2 text-center">
                          <button
                            type="button"
                            data-testid={`fix-lineup-${L.lineup_id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFixingId((cur) => (cur === L.lineup_id ? null : L.lineup_id));
                              if (!expanded[L.lineup_id]) setExpanded({ ...expanded, [L.lineup_id]: true });
                            }}
                            title={L.fixed_at ? `Re-solved (${L.fix_mode}) — click to fix again` : "Fix this lineup — swap a player or rebuild it in place"}
                            className={`inline-flex items-center justify-center w-6 h-6 border transition-colors ${
                              fixingId === L.lineup_id ? "bg-[#FFCC00] text-black border-[#FFCC00]" :
                              L.fixed_at ? "border-[#FFCC00]/60 text-[#FFCC00]" :
                              "border-zinc-800 text-zinc-500 hover:border-[#FFCC00] hover:text-[#FFCC00]"
                            }`}
                          >
                            <Wrench size={11} weight={L.fixed_at ? "fill" : "bold"} />
                          </button>
                        </td>
                      )}
                    </tr>
                    {expanded[L.lineup_id] && (
                      <tr className="bg-black/40">
                        <td colSpan={11} className="px-6 py-3">
                          {source === "smart" && fixingId === L.lineup_id && (
                            <FixLineupPanel
                              lineup={L}
                              pool={pool}
                              onClose={() => setFixingId(null)}
                              onFixed={() => { setFixingId(null); load(); }}
                            />
                          )}
                          {mc && !mc.error && (
                            <PercentileBar mc={mc} lineupId={L.lineup_id} />
                          )}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            {sortLineupPlayers(L.players).map((p, slotIdx) => {
                              const isFocused = focusedPlayer && String(focusedPlayer.dk_id) === String(p.dk_id);
                              // Compute the DK slot label so the user sees "FLEX"
                              // on the extra RB/WR/TE (not their raw position).
                              const isShowdown = p.roster_slot === "CPT" || L.players.some((x) => x.roster_slot === "CPT");
                              let slotLabel;
                              if (isShowdown) {
                                slotLabel = p.roster_slot === "CPT" ? "CPT" : "FLEX";
                              } else {
                                // Slot order matches sortLineupPlayers output:
                                // 0=QB, 1-2=RB, 3-5=WR, 6=TE, 7=FLEX, 8=DST
                                const dkSlots = ["QB", "RB", "RB", "WR", "WR", "WR", "TE", "FLEX", "DST"];
                                slotLabel = dkSlots[slotIdx] || (p.position || "").toUpperCase();
                              }
                              return (
                              <div key={p.dk_id} className={`flex justify-between items-center border ${p.roster_slot === "CPT" ? "border-[#FF3B30] bg-[#FF3B30]/5" : isFocused ? "border-[#39FF14] bg-[#39FF14]/5" : "border-zinc-900"} px-3 py-2`}>
                                <div className="flex items-center gap-2">
                                  <PlayerHeadshot player={p} sleeperId={headshots[String(p.dk_id)]} size={26} />
                                  {p.roster_slot === "CPT" && (
                                    <span className="text-[9px] mono px-1.5 py-0.5 border border-[#FF3B30] text-[#FF3B30] font-bold">CPT · 1.5×</span>
                                  )}
                                  <span
                                    className={`text-[10px] mono uppercase mr-2 font-bold tracking-wider ${slotLabel === "FLEX" ? "text-[#FF8A00]" : "text-zinc-500"}`}
                                    title={slotLabel === "FLEX" ? `${p.position} playing FLEX slot` : `${slotLabel} slot`}
                                  >
                                    {slotLabel}
                                  </span>
                                  <span className="font-medium">{p.name}</span>
                                  <button
                                    type="button"
                                    data-testid={`focus-player-${p.dk_id}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setFocusedPlayer((cur) =>
                                        cur && String(cur.dk_id) === String(p.dk_id)
                                          ? null
                                          : { dk_id: p.dk_id, name: p.name }
                                      );
                                    }}
                                    title={isFocused
                                      ? `Currently filtering — click to show all lineups again`
                                      : `Show only lineups containing ${p.name}`}
                                    className={`ml-1 flex items-center justify-center w-5 h-5 transition-colors ${isFocused ? "text-[#39FF14]" : "text-zinc-600 hover:text-[#39FF14]"}`}
                                  >
                                    <MagnifyingGlass size={12} weight={isFocused ? "fill" : "regular"} />
                                  </button>
                                </div>
                                <div className="text-right">
                                  <div className={`mono ${p.roster_slot === "CPT" ? "text-[#FF3B30]" : "text-zinc-300"}`}>${(p.salary || 0).toLocaleString()}</div>
                                  <div className="text-[10px] text-zinc-500 mono">
                                    <span className="font-black" style={{ color: teamTextColor(p.team) }}>{p.team}</span> · {(Number(p.market_mean_own) > 0 ? Number(p.market_mean_own) : Number(p.our_own || 0)).toFixed(1)}%
                                  </div>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="xl:col-span-4 bg-[#121212] border border-zinc-800">
          <div className="px-4 py-3 border-b border-zinc-800">
            <div className="overline">Player Exposure</div>
            <div className="text-sm text-zinc-300 mt-1">Across the {activeData.count}-lineup pool</div>
          </div>
          <div className="max-h-[720px] overflow-y-auto overflow-x-auto">
            <table data-testid={TID.exposureTable} className="w-full min-w-[320px] text-xs mono">
              <tbody>
                {exposureRows.map((r) => (
                  <tr key={r.id} className="border-t border-zinc-900">
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-[10px] text-zinc-500 uppercase">{r.pos} · {r.team}</div>
                    </td>
                    <td className="px-3 py-2 text-right w-24">
                      <div className={r.pct > 25 ? "text-[#FFCC00]" : "text-[#39FF14]"}>{r.pct.toFixed(1)}%</div>
                      <div className="text-[10px] text-zinc-500">{r.count}/{activeData.count}</div>
                    </td>
                    <td className="px-3 py-2 w-32">
                      <div className="h-1 bg-zinc-900">
                        <div className={`h-full ${r.pct > 25 ? "bg-[#FFCC00]" : "bg-[#39FF14]"}`} style={{ width: `${Math.min(100, r.pct)}%` }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </aside>
      </div>

      {source === "smart" && <CandidateExplorer onChanged={load} />}

      <ResultsScoreboard />

      <LateInjuryModal open={showInjury} onClose={() => setShowInjury(false)} pool={pool} onApplied={load} />
    </div>
  );
}

// Sprint 60.140 — Source toggle + at-a-glance portfolio diff between
// Smart and Raw. Users optimize on both engines and land here to
// compare rather than a full-page view-all with no tab context. The
// diff row highlights the practical differences (avg proj, ownership,
// unique players, salary spread) so the "what is Smart doing that Raw
// isn't?" question is answerable in one look.
function SourceToggleAndCompare({ source, setSource, smart, raw, auto, contestFilter, setContestFilter }) {
  const summarise = (batch) => {
    const list = batch?.lineups || [];
    const n = list.length;
    if (!n) return { n: 0, avgProj: 0, avgOwn: 0, avgSal: 0, uniquePlayers: 0 };
    let totalProj = 0, totalOwn = 0, totalSal = 0;
    const seen = new Set();
    for (const L of list) {
      totalProj += Number(L.projection || 0);
      totalOwn += Number(L.total_own || L.own_sum || 0);
      totalSal += Number(L.salary || 0);
      for (const p of (L.players || [])) {
        if (p.dk_id != null) seen.add(String(p.dk_id));
      }
    }
    return {
      n,
      avgProj: totalProj / n,
      avgOwn: totalOwn / n,
      avgSal: totalSal / n,
      uniquePlayers: seen.size,
    };
  };
  const s = summarise(smart);
  const r = summarise(raw);
  const a = summarise(auto);
  const active = source === "raw" ? r : s;
  const other  = source === "raw" ? s : r;
  const otherLabel = source === "raw" ? "Smart" : "Raw";

  const Tab = ({ id, label, count, isActive, disabled }) => (
    <button
      type="button"
      data-testid={`lineups-source-${id}`}
      onClick={() => !disabled && setSource(id)}
      disabled={disabled}
      className={`inline-flex items-center gap-2 px-4 py-2.5 border font-bold uppercase tracking-widest text-[11px] transition-colors ${
        isActive
          ? "bg-[#39FF14] text-black border-[#39FF14] shadow-[0_0_16px_-6px_#39FF14]"
          : disabled
            ? "bg-transparent border-zinc-800 text-zinc-600 cursor-not-allowed"
            : "bg-transparent border-zinc-700 text-zinc-300 hover:border-[#39FF14] hover:text-[#39FF14]"
      }`}
      title={disabled ? `Generate ${label} lineups first on the ${id === "smart" ? "Smart" : "Raw"} Optimizer tab` : `Show ${label} portfolio`}
    >
      {label}
      <span className={`mono text-[10px] tracking-widest ${
        isActive ? "text-black/70" : disabled ? "text-zinc-600" : "text-zinc-500"
      }`}>
        {count > 0 ? `· ${count}` : "· 0"}
      </span>
    </button>
  );

  const Cell = ({ label, value, sub, tone = "neutral" }) => (
    <div className="bg-black/40 border border-zinc-900 px-3 py-2.5 min-w-0">
      <div className="overline text-[9px] tracking-widest text-zinc-500 uppercase truncate">{label}</div>
      <div className={`mono font-black text-lg leading-tight mt-0.5 ${
        tone === "good" ? "text-[#39FF14]" :
        tone === "warn" ? "text-[#FFCC00]" :
        tone === "bad"  ? "text-[#FF3B30]" :
                          "text-white"
      }`}>{value}</div>
      {sub && <div className="text-[9px] text-zinc-600 mt-0.5 mono truncate">{sub}</div>}
    </div>
  );

  const fmtN = (v) => (v == null || Number.isNaN(v)) ? "—" : v.toFixed(1);
  const fmtInt = (v) => (v == null) ? "—" : Math.round(v).toLocaleString();
  const fmtDollarsNum = (v) => (v == null) ? "—" : `$${Math.round(v).toLocaleString()}`;

  return (
    <section
      data-testid="lineups-source-toggle"
      className="bg-[#121212] border border-zinc-800"
    >
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Tab id="smart" label="Smart Optimized" count={s.n} isActive={source === "smart"} disabled={s.n === 0} />
          <Tab id="raw"   label="Manual Lineups"   count={r.n} isActive={source === "raw"}   disabled={r.n === 0} />
          <Tab id="auto"  label="Auto-Sense Build"  count={a.n} isActive={source === "auto"}  disabled={a.n === 0} />
          {source === "auto" && (auto?.contests || []).length > 0 && (
            <select
              data-testid="lineups-auto-contest-filter"
              value={contestFilter}
              onChange={(e) => setContestFilter(e.target.value)}
              className="bg-black border border-zinc-700 text-zinc-200 mono text-[10px] uppercase tracking-widest px-2 py-2 focus:border-[#39FF14] outline-none max-w-[320px]"
            >
              <option value="all">All tournaments · {auto.count} entries</option>
              {(auto.contests || []).map((c) => (
                <option key={c.contest_id} value={String(c.contest_id)}>
                  {c.contest_name} · {c.generated}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="text-[10px] mono uppercase tracking-widest text-zinc-500 max-w-md text-right">
          {source === "smart"
            ? "Smart · contest rules, stack shapes, ownership leverage and portfolio dedupe applied"
            : source === "auto"
              ? `Auto-sense · ${(auto?.contests || []).length} tournament(s) · each batch tuned for its own field size and max-entries rule`
              : "Raw · pure ILP · only your explicit locks / excludes / group rules applied"}
        </div>
      </div>
      <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-5 gap-2">
        <Cell label={`${source === "smart" ? "Smart" : "Raw"} Lineups`}
              value={fmtInt(active.n)}
              sub={other.n > 0 ? `${otherLabel} has ${fmtInt(other.n)}` : `${otherLabel}: none yet`} />
        <Cell label="Avg Projection"
              value={fmtN(active.avgProj)}
              tone="good"
              sub={other.n > 0 ? `vs ${otherLabel} ${fmtN(other.avgProj)}` : undefined} />
        <Cell label="Avg Total Own %"
              value={`${fmtN(active.avgOwn)}%`}
              tone={active.avgOwn > 130 ? "warn" : "neutral"}
              sub={other.n > 0 ? `vs ${otherLabel} ${fmtN(other.avgOwn)}%` : undefined} />
        <Cell label="Avg Salary"
              value={fmtDollarsNum(active.avgSal)}
              sub={other.n > 0 ? `vs ${otherLabel} ${fmtDollarsNum(other.avgSal)}` : undefined} />
        <Cell label="Unique Players"
              value={fmtInt(active.uniquePlayers)}
              sub={other.n > 0 ? `${otherLabel} used ${fmtInt(other.uniquePlayers)}` : undefined} />
      </div>
      <div className="px-4 pb-3 pt-1 text-[11px] text-zinc-500 leading-relaxed">
        <span className="text-zinc-400 font-semibold">Smart vs Manual · what's different:</span>{" "}
        Smart applies WarPaint's contest rule engine — it chooses stack shapes, Vegas favorites
        bias QB choice, every lineup carries low-owned pivots, and portfolio dedupe
        removes near-identical builds. Raw skips all of that and solves the pure ILP against only
        your explicit locks, excludes, boosts, and group rules — great for testing your own theory or matching
        another tool's build. Higher avg ownership and less variety usually mean Raw is chasing max-projection
        without the leverage rules Smart enforces.
      </div>
    </section>
  );
}


function ActionBar({ onInjury, activeOuts, onClear, pool, rightSlot }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-3">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          data-testid={TID.lateInjuryBtn}
          onClick={onInjury}
          className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#e0342a] text-white font-bold uppercase tracking-widest text-xs py-3 px-5 transition-colors"
        >
          <Warning size={14} weight="fill" />
          Late Injury Out
        </button>
        {activeOuts.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="overline">Active outs:</span>
            {activeOuts.map((id) => {
              const p = pool.find((x) => String(x.dk_id) === String(id));
              return (
                <span key={id} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-[#FF3B30] text-[#FF3B30] mono">
                  {p?.name || id}
                  <button onClick={() => onClear(id)} className="hover:text-white transition-colors">
                    <X size={12} />
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>
      {rightSlot}
    </div>
  );
}

function StackTypeBadge({ lineup, type }) {
  // Sprint 60.112 — accept either the full lineup (preferred, uses smart
  // player-based label) or just the type string (fallback for legacy
  // callers). Structural shape drives the color; user-facing label comes
  // from `humanStackLabel` which reads the actual player composition.
  const t = type || lineup?.stack_type;
  const colorMap = {
    primary_only: "bg-zinc-700 text-white",
    primary_secondary: "bg-[#007AFF] text-white",
    primary_secondary_tertiary: "bg-[#39FF14] text-black",
  };
  const color = colorMap[t] || "bg-zinc-800 text-zinc-300";
  const label = lineup ? humanStackLabel(lineup) : (t || "").replace(/_/g, " ");
  return <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold whitespace-nowrap ${color}`}>{label}</span>;
}

function RangeOfOutcomesCard({ ranges, loading }) {
  if (loading) {
    return (
      <div data-testid="range-of-outcomes-card" className="bg-[#121212] border border-zinc-800 p-4 flex items-center gap-3">
        <ChartLineUp size={16} className="text-zinc-600 animate-pulse" />
        <div className="overline">Running 25K per-player Monte Carlo simulations…</div>
      </div>
    );
  }
  if (!ranges || !ranges.outcomes || !ranges.outcomes.length) return null;

  const outcomes = ranges.outcomes.filter((o) => !o.error);
  if (!outcomes.length) return null;
  const nSims = (ranges.n_sims || 25000).toLocaleString();
  const avg = (k) => outcomes.reduce((s, o) => s + (o[k] || 0), 0) / outcomes.length;
  const p5 = avg("p5"), p50 = avg("p50"), p95 = avg("p95"), boom = avg("boom_pct");
  // Top-1% ceiling — best 99th percentile across the portfolio
  const bestCeiling = Math.max(...outcomes.map((o) => o.p99 || 0));

  return (
    <section
      data-testid="range-of-outcomes-card"
      className="bg-gradient-to-br from-[#0f1a12] via-[#0d0d0d] to-[#0a0a0a] border border-[#39FF14]/25 relative overflow-hidden"
      style={{ boxShadow: "0 0 32px rgba(57,255,20,0.06), inset 0 1px 0 rgba(255,255,255,0.03)" }}
    >
      <div className="absolute inset-0 pointer-events-none opacity-40"
           style={{ background: "radial-gradient(600px 120px at 20% 0%, rgba(57,255,20,0.12), transparent 70%)" }} />
      <div className="relative px-5 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ArrowsOut size={14} weight="fill" className="text-[#39FF14]" />
              <div className="overline text-[#39FF14]">Range of Outcomes · Deep Sim</div>
            </div>
            <div className="text-sm text-zinc-300 mt-1">
              {nSims} per-player Monte Carlo iterations · position-calibrated variance (nflfastR 2020-2026)
            </div>
          </div>
          <div className="text-[10px] mono text-zinc-500 uppercase tracking-widest">
            {outcomes.length} lineups simulated
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <Stat label="Portfolio Floor (p5)" value={p5.toFixed(1)} accent="text-zinc-300" sub="5th percentile avg" />
          <Stat label="Median (p50)" value={p50.toFixed(1)} accent="text-white" sub="50th percentile avg" />
          <Stat label="Ceiling (p95)" value={p95.toFixed(1)} accent="text-[#39FF14]" sub="95th percentile avg" />
          <Stat label="Boom Rate" value={`${boom.toFixed(1)}%`} accent="text-[#39FF14]" sub="≥130% of mean · avg" />
        </div>

        <div className="mt-4 border-t border-zinc-800 pt-3 text-[11px] text-zinc-500 leading-relaxed">
          Best single-lineup 99th percentile ceiling: <span className="mono text-[#39FF14] font-bold">{bestCeiling.toFixed(1)}</span> pts.
          <span className="text-zinc-600"> · Boom = % of sims scoring ≥ 130% of lineup mean. Higher = more tournament-shaped variance.</span>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, accent, sub }) {
  return (
    <div className="bg-black/40 border border-zinc-900 px-3 py-2.5">
      <div className="overline text-[9px]">{label}</div>
      <div className={`mono font-black text-2xl leading-none mt-1 ${accent}`}>{value}</div>
      {sub && <div className="text-[9px] text-zinc-600 mt-1 mono tracking-wider">{sub}</div>}
    </div>
  );
}

function PercentileBar({ mc, lineupId }) {
  // Visual percentile spread: p5 — p25 — p50 — p75 — p95, positioned on a shared axis
  const { p5, p25, p50, p75, p95, boom_pct, bust_pct } = mc;
  const range = Math.max(p95 - p5, 1);
  const pct = (v) => ((v - p5) / range) * 100;
  return (
    <div data-testid={`range-bar-${lineupId}`} className="mb-3 bg-black/40 border border-zinc-900 px-4 py-3">
      <div className="flex items-center justify-between mb-2">
        <div className="overline text-[#39FF14]">Range of Outcomes · 25K sims</div>
        <div className="flex items-center gap-3 mono text-[10px]">
          <span className="text-[#39FF14]">Boom {boom_pct?.toFixed(1)}%</span>
          <span className="text-[#FF3B30]">Bust {bust_pct?.toFixed(1)}%</span>
        </div>
      </div>
      <div className="relative h-2 bg-zinc-900 rounded-sm overflow-hidden">
        {/* 5-95 band */}
        <div className="absolute inset-y-0" style={{ left: 0, right: 0, background: "linear-gradient(90deg, #FF3B30 0%, #FFCC00 50%, #39FF14 100%)", opacity: 0.35 }} />
        {/* p25-p75 highlight band */}
        <div className="absolute inset-y-0 bg-white/10 border-x border-white/20" style={{ left: `${pct(p25)}%`, width: `${pct(p75) - pct(p25)}%` }} />
        {/* p50 median marker */}
        <div className="absolute inset-y-0 w-0.5 bg-white" style={{ left: `${pct(p50)}%` }} title={`Median ${p50.toFixed(1)}`} />
      </div>
      <div className="grid grid-cols-5 gap-1 mt-2 mono text-[10px] text-center">
        <PercentileLabel label="p5" value={p5} color="text-[#FF3B30]" />
        <PercentileLabel label="p25" value={p25} color="text-[#FFCC00]" />
        <PercentileLabel label="p50" value={p50} color="text-white" />
        <PercentileLabel label="p75" value={p75} color="text-[#39FF14]" />
        <PercentileLabel label="p95" value={p95} color="text-[#39FF14]" />
      </div>
    </div>
  );
}

function PercentileLabel({ label, value, color }) {
  return (
    <div>
      <div className="text-[9px] text-zinc-600 uppercase tracking-widest">{label}</div>
      <div className={`font-bold ${color}`}>{value?.toFixed(1)}</div>
    </div>
  );
}


